import CtaButton from '../ui/buttons/CtaButton';
import Cta from '@/components/ui/buttons/Cta';
import React from 'react';
import breakpoint from 'styled-components-breakpoint';
import styled from 'styled-components';
import { theme } from 'styled-tools';
import styles from './styles.module.css';
import { imageUrlBuilder } from '@/utils/helpers';

const Content = styled.div<{ overlay?: boolean }>`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => (props.overlay ? 'flex-start' : 'center')};
  align-items: center;
  flex-wrap: wrap;
  height: ${(props) => (props.overlay ? '540px' : '332px')};

  ${breakpoint('xs', 'sm')`
    height: ${(props) => (props.overlay ? '525px' : ' auto')};
    justify-content: center;
  `}
`;

const Background = styled.div<{ background: string; overlay?: boolean }>`
  background: ${(props) => props.background};
  background-size: cover;
  background-position: right top;
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  object-fit: cover;
  width: 100%;
  height: auto;
  min-height: ${(props) => (props.overlay ? '540px' : '332px')};

  ${breakpoint('xs', 'sm')`
    min-height: 100%;
  `}
`;

const SecondaryText = styled.div<{ overlay?: boolean }>`
  width: 100%;
  padding: 2em 1.6em 5.4em;

  & p {
    color: ${(props) => (props.overlay ? theme('colors.white') : theme('colors.blue'))};
    width: 100%;
    margin: 0;
    padding: 0;
    font-size: 20px;
    font-weight: 400;
  }

  .cta-wrapper {
    margin-top: 20px;
  }

  ${breakpoint('md')`
    width: 50%;
    padding: 2em;

    & p {
      width: 80%;
    }
  `}
  ${breakpoint('sm', 'md')`
    width: 50%;
    padding: 2em;

    & p {
      width: 90%;
    }
  `}
`;

const Overlay = styled.div<{ overlay?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;
  width: 100%;
  height: ${(props) => (props.overlay ? '80%' : ' auto')};
  padding: 5.4em 1.6em 0;
  background: ${(props) => (props.overlay ? 'rgba(47,63,86,0.9)' : 'transparent')};
  max-width: ${(props) => (props.overlay ? '510px' : '100%')};

  & h2 {
    width: ${(props) => (props.overlay ? '100%' : '80%')};
    color: ${(props) => (props.overlay ? theme('colors.white') : theme('colors.blue'))};
    margin: 0;
    padding: 0;
    font-size: 36px;
    font-weight: 700;
  }

  .cta-wrapper {
    width: 100%;
    padding: 0 4em 0 0;
    position: relative;
    color: #f88143;
    margin: 2rem auto;
  }

  ${breakpoint('md')`
    padding: 2rem 6rem;
    width: 50%;
  `}
  ${breakpoint('sm', 'md')`
    padding: 2em 3.8em;
    width: 50%;
  `}
  ${breakpoint('xs', 'sm')`
    padding: ${(props) => (props.overlay ? '4.5em 1.6em 3.5em' : '5.4em 1.6em 0')};
  `}
`;

interface TextCta {
  url: string;
  text: string;
  _type: string;
}

interface BannerProps {
  heading?: string;
  cta?: Record<string, unknown>;
  textCta?: TextCta;
  textColor?: Record<string, unknown>;
  bgColor?: Record<string, unknown>;
  bgGradient?: Record<string, unknown>;
  bgImage?: Record<string, unknown>;
  alignment?: string;
  primaryText?: string;
  secondaryText?: string;
  overlay?: boolean;
  background?: string;
  mode?: string;
  primaryTextColor?: Record<string, unknown>;
}

const Banner: React.FC<BannerProps> = ({
  heading,
  cta,
  textCta,
  textColor,
  bgColor,
  bgGradient,
  bgImage,
  primaryText,
  primaryTextColor,
  secondaryText,
  overlay = true,
  background,
}) => {
  const imgUrl =
    bgImage &&
    `url(${imageUrlBuilder
      .image((bgImage as { asset: unknown }).asset)
      .width(1920)
      .auto('format')
      .quality(80)
      .url()})`;

  return (
    <section
      className={styles.banner}
      style={{
        backgroundColor:
          (bgColor && (bgColor as { hex: string }).hex) ||
          (bgGradient &&
            `-webkit-linear-gradient(
            ${
              (
                bgGradient as {
                  gradientDirection: string;
                  colorStart: { hex: string };
                  colorStop: { hex: string };
                }
              ).gradientDirection
            },
            ${
              (
                bgGradient as {
                  gradientDirection: string;
                  colorStart: { hex: string };
                  colorStop: { hex: string };
                }
              ).colorStart.hex
            } 0%,
            ${
              (
                bgGradient as {
                  gradientDirection: string;
                  colorStart: { hex: string };
                  colorStop: { hex: string };
                }
              ).colorStop.hex
            } 100%)`),
        backgroundImage: imgUrl,
        color: (textColor && (textColor as { hex: string }).hex) || '#FFF',
      }}
    >
      <Background background={background} overlay={overlay}></Background>
      <Content overlay={overlay}>
        <Overlay overlay={overlay}>
          <h2
            style={{
              color: (primaryTextColor as { hex?: string } | undefined)?.hex,
            }}
          >
            {primaryText || heading}
          </h2>
          {cta && (
            <div className="cta-wrapper">
              <CtaButton
                to={(cta as { url?: string; route?: string }).url || (cta as { url?: string; route?: string }).route}
                className="cta-shadow"
              >
                {(cta as { text?: string; title?: string }).text || (cta as { text?: string; title?: string }).title}
              </CtaButton>
            </div>
          )}
        </Overlay>
        {secondaryText && (
          <SecondaryText>
            <p>{secondaryText}</p>
            {textCta && (
              <div className="cta-wrapper">
                <Cta to={textCta.url} type={textCta._type || 'link'} hoverMode="dark">
                  {textCta.text}
                </Cta>
              </div>
            )}
          </SecondaryText>
        )}
      </Content>
    </section>
  );
};

export default Banner;
